import React, { useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { Download } from "@mui/icons-material";
import { useLazyListGeneralLedgerQuery } from "store/services/generalLedgerApi";
import { toast } from "react-toastify";
import moment from "moment";
import * as XLSX from "xlsx";

const ExportGeneralLedger = ({ filters }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [triggerExport] = useLazyListGeneralLedgerQuery();

  const hasFilter =
    !!filters?.keyword ||
    !!filters?.fromDate ||
    !!filters?.toDate ||
    !!filters?.serviceChargePayer ||
    !!filters?.event ||
    !!filters?.glAccount;

  const handleExport = async () => {
    try {
      setIsExporting(true);

      const queryParams = {
        keyword: filters.keyword || "",
        fromDate: filters.fromDate || "",
        toDate: filters.toDate || "",
        serviceChargePayer: filters.serviceChargePayer || "",
        event: filters.event || "",
        glAccount: filters.glAccount || "",
        limit: Number.MAX_SAFE_INTEGER,
        page: 1,
      };

      const response = await triggerExport(queryParams).unwrap();

      let exportData = null;
      if (response?.data?.data) {
        exportData = response.data.data;
      } else if (response?.data) {
        exportData = response.data;
      } else if (Array.isArray(response)) {
        exportData = response;
      }

      if (!exportData || !Array.isArray(exportData) || exportData.length === 0) {
        toast.info("No data to export");
        return;
      }

      generateExcel(exportData);
      toast.success(`Exported ${exportData.length} records successfully`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed: " + (error?.message || "Unknown error"));
    } finally {
      setIsExporting(false);
    }
  };

  const generateExcel = (data) => {
    const hasCocId = data.some((row) => row.cocCollection?.cocCollectionId);
    const hasCodId = data.some((row) => row.codTransfer?.codTransferId);
    const hasDeliveryId = data.some((row) => row.deliveryCollection?.deliveryCollectionId);

    // SN removed. NPR moved into Debit/Credit headers.
    const headers = [
      "GL Number",                 // 0 — merged
      ...(hasCocId ? ["COC ID"] : []),   // merged (if present)
      ...(hasCodId ? ["COD ID"] : []),   // merged (if present)
      ...(hasDeliveryId ? ["Delivery ID"] : []), // merged (if present)
      "Shipment",                  // merged
      "Transaction Description",   // merged
      "Payer",                     // merged
      "Account",                   // NOT merged
      "Debit Amount (NPR)",        // NOT merged
      "Credit Amount (NPR)",       // NOT merged
      "Created At",                // merged
      "Shipment Created Date",     // merged
      "Shipment Reference",        // LAST RIGHT SIDE
    ];

    // Compute column indices (SN removed, so GL Number starts at 0)
    let dynOffset = 1;
    if (hasCocId) dynOffset++;
    if (hasCodId) dynOffset++;
    if (hasDeliveryId) dynOffset++;

    const COL_GL = 0;
    const COL_COC = hasCocId ? 1 : -1;
    const COL_COD = hasCodId ? (hasCocId ? 2 : 1) : -1;
    const COL_DELIVERY = hasDeliveryId
      ? 1 + (hasCocId ? 1 : 0) + (hasCodId ? 1 : 0)
      : -1;
    const COL_SHIPMENT = dynOffset;
    const COL_DESC = dynOffset + 1;
    const COL_PAYER = dynOffset + 2;
    const COL_ACCOUNT = dynOffset + 3;
    const COL_DEBIT = dynOffset + 4;
    const COL_CREDIT = dynOffset + 5;
    const COL_CREATED = dynOffset + 6;
    const COL_SHIPMENT_DATE = dynOffset + 7;
    const COL_SHIPMENT_REF = dynOffset + 8;

    const wsData = [headers];

    data.forEach((row) => {
      const debits = row.debits || [];
      const credits = row.credits || [];

      // Use moment.utc(...) — not moment(...) — so the displayed timestamp
      // matches the UTC value the backend actually filtered on. Plain
      // moment(isoString).format() silently converts to the browser's local
      // timezone, which can push a record into the "wrong" calendar day for
      // users ahead of UTC (e.g. Asia/Kathmandu, UTC+5:45).
      const createdAt = row.createdAt
        ? moment.utc(row.createdAt).format("YYYY-MM-DD")
        : "N/A";

      // Shared values duplicated on both the debit and credit row
      const glNumber = row.glNumber || "N/A";
      const cocId = row.cocCollection?.cocCollectionId || "N/A";
      const codId = row.codTransfer?.codTransferId || "N/A";
      const deliveryId = row.deliveryCollection?.deliveryCollectionId || "N/A";
      const shipment = Array.isArray(row.shipment) ? row.shipment[0] : row.shipment;
      const shipmentId = shipment?.shipmentId || "N/A";

      const rawShipmentDate =
        shipment?.createdAt ||
        (Array.isArray(row.codTransfer?.shipmentId) ? row.codTransfer.shipmentId[0]?.createdAt : null) ||
        (Array.isArray(row.deliveryCollection?.shipment) ? row.deliveryCollection.shipment[0]?.createdAt : null) ||
        (Array.isArray(row.cocCollection?.shipmentId) ? row.cocCollection.shipmentId[0]?.createdAt : null);

      const shipmentDate = rawShipmentDate && moment(rawShipmentDate).isValid()
        ? moment.utc(rawShipmentDate).format("YYYY-MM-DD")
        : "N/A";

      const shipmentReference =
        row.shipmentReference ||
        (Array.isArray(row.codTransfer?.shipmentId)
          ? row.codTransfer.shipmentId
              .map((s) => (typeof s === "object" ? s?.shipmentId : s))
              .filter(Boolean)
              .join(", ")
          : null) ||
        (Array.isArray(row.deliveryCollection?.shipment)
          ? row.deliveryCollection.shipment
              .map((s) => (typeof s === "object" ? s?.shipmentId : s))
              .filter(Boolean)
              .join(", ")
          : null) ||
        (Array.isArray(row.cocCollection?.shipmentId)
          ? row.cocCollection.shipmentId
              .map((s) => (typeof s === "object" ? s?.shipmentId : s))
              .filter(Boolean)
              .join(", ")
          : null) ||
        shipmentId ||
        "N/A";

      const description = row.events || "N/A";
      const payer = row.serviceChargePayer || "N/A";

      // ── Debit row ────────────────────────────────────────────────────
      const debitRow = new Array(headers.length).fill("");
      debitRow[COL_GL] = glNumber;
      if (hasCocId) debitRow[COL_COC] = cocId;
      if (hasCodId) debitRow[COL_COD] = codId;
      if (hasDeliveryId) debitRow[COL_DELIVERY] = deliveryId;
      debitRow[COL_SHIPMENT] = shipmentId;
      debitRow[COL_DESC] = description;
      debitRow[COL_PAYER] = payer;
      debitRow[COL_ACCOUNT] = debits[0] ? (debits[0].account || "") : "—";
      debitRow[COL_DEBIT] = debits[0] ? debits[0].amount : "";
      debitRow[COL_CREDIT] = ""; // not applicable on the debit row
      debitRow[COL_CREATED] = createdAt;
      debitRow[COL_SHIPMENT_DATE] = shipmentDate;
      debitRow[COL_SHIPMENT_REF] = shipmentReference;

      // ── Credit row ───────────────────────────────────────────────────
      const creditRow = new Array(headers.length).fill("");
      creditRow[COL_GL] = glNumber;
      if (hasCocId) creditRow[COL_COC] = cocId;
      if (hasCodId) creditRow[COL_COD] = codId;
      if (hasDeliveryId) creditRow[COL_DELIVERY] = deliveryId;
      creditRow[COL_SHIPMENT] = shipmentId;
      creditRow[COL_DESC] = description;
      creditRow[COL_PAYER] = payer;
      creditRow[COL_ACCOUNT] = credits[0] ? (credits[0].account || "") : "—";
      creditRow[COL_DEBIT] = ""; // not applicable on the credit row
      creditRow[COL_CREDIT] = credits[0] ? credits[0].amount : "";
      creditRow[COL_CREATED] = createdAt;
      creditRow[COL_SHIPMENT_DATE] = shipmentDate;
      creditRow[COL_SHIPMENT_REF] = shipmentReference;

      wsData.push(debitRow);
      wsData.push(creditRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Apply "#,##0.00" format to Debit/Credit amount columns so Excel's
    // status bar can show Count / Sum / Average automatically when cells
    // are selected (works because cells now hold real numbers, not text).
    const dataRange = XLSX.utils.decode_range(ws["!ref"]);
    const amountCols = [COL_DEBIT, COL_CREDIT];
    for (let R = 1; R <= dataRange.e.r; R++) {
      amountCols.forEach((C) => {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[cellRef] && typeof ws[cellRef].v === "number") {
          ws[cellRef].z = "#,##0.00";
        }
      });
    }

    ws["!cols"] = [
      { wch: 15 },  // GL Number
      ...(hasCocId ? [{ wch: 15 }] : []),
      ...(hasCodId ? [{ wch: 15 }] : []),
      ...(hasDeliveryId ? [{ wch: 15 }] : []),
      { wch: 18 },  // Shipment
      { wch: 35 },  // Transaction Description
      { wch: 18 },  // Payer
      { wch: 25 },  // Account
      { wch: 18 },  // Debit Amount (NPR)
      { wch: 18 },  // Credit Amount (NPR)
      { wch: 22 },  // Created At (UTC)
      { wch: 22 },  // Shipment Created Date
      { wch: 32 },  // Shipment Reference (last right side)
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "General Ledger");

    const fileName = `General_Ledger_${moment().format("YYYYMMDD")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Tooltip title={!hasFilter ? "Please apply a filter to export" : ""} arrow>
      <span>
        <Button
          startIcon={<Download />}
          variant="outlined"
          onClick={handleExport}
          disabled={!hasFilter || isExporting}
          size="small"
        >
          {isExporting ? "Exporting..." : "Export"}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ExportGeneralLedger;
